import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as api from "../services/api";

// ─── Types ────────────────────────────────────────────────────────────────
type Mode =
  | "zero_based"
  | "envelope"
  | "subscriptions"
  | "forecast"
  | "net_worth"
  | "debt_payoff"
  | "retirement"
  | "savings_goals"
  | "emergency_fund"
  | "transaction_sync"
  | "household_sync";

interface Expense { category: string; amount: number; suggested_pct?: number }
interface Envelope { name: string; allocated: number; spent: number }
interface Subscription { name: string; monthly_cost: number; category: string; usage_per_week: number }
interface Asset { name: string; value: number; type: string }
interface Liability { name: string; balance: number; interest_rate: number; type: string }
interface Debt { name: string; balance: number; apr: number; min_payment: number }
interface SavingsGoal { name: string; target_amount: number; current_amount: number; priority: number; deadline_months: number }
interface Txn { merchant: string; amount: number; category: string; account: string; date: string }
interface HouseholdMember { name: string }
interface SharedCategory { name: string; limit: number }
interface SharedTxn { member: string; category: string; amount: number; date: string; note?: string }

const TABS: { mode: Mode; label: string; icon: string; desc: string }[] = [
  { mode: "zero_based", label: "Zero-Based Budget", icon: "🎯", desc: "YNAB-style — assign every dollar a job" },
  { mode: "envelope", label: "Envelope System", icon: "✉️", desc: "Goodbudget-style — digital envelopes" },
  { mode: "subscriptions", label: "Subscription Audit", icon: "🔍", desc: "Rocket Money-style — find & cut waste" },
  { mode: "forecast", label: "12-Month Forecast", icon: "📈", desc: "Monarch Money-style — project your future" },
  { mode: "net_worth", label: "Net Worth Tracker", icon: "💰", desc: "Empower-style — assets vs liabilities" },
  { mode: "debt_payoff", label: "Debt Payoff", icon: "🧨", desc: "Snowball vs Avalanche — kill debt faster" },
  { mode: "retirement", label: "FIRE Calculator", icon: "🔥", desc: "Origin-style retirement and FIRE planning" },
  { mode: "savings_goals", label: "Savings Goals", icon: "🎯", desc: "Track and fund multiple goals" },
  { mode: "emergency_fund", label: "Emergency Fund", icon: "🛟", desc: "3–6 month safety net planner" },
  { mode: "transaction_sync", label: "Auto Transaction Sync", icon: "🔄", desc: "Monarch-style synced spend intelligence" },
  { mode: "household_sync", label: "Household Sync", icon: "🤝", desc: "Collaborative budgeting for families" },
];

const EXPENSE_SUGGESTIONS = [
  { category: "Housing / Rent", amount: 1500, suggested_pct: 30 },
  { category: "Groceries", amount: 400, suggested_pct: 10 },
  { category: "Transportation", amount: 300, suggested_pct: 10 },
  { category: "Utilities", amount: 150, suggested_pct: 5 },
  { category: "Dining Out", amount: 200, suggested_pct: 5 },
  { category: "Entertainment", amount: 100, suggested_pct: 3 },
  { category: "Healthcare", amount: 100, suggested_pct: 3 },
  { category: "Clothing", amount: 80, suggested_pct: 2 },
  { category: "Personal Care", amount: 60, suggested_pct: 2 },
  { category: "Miscellaneous", amount: 100, suggested_pct: 3 },
];

const SUB_SUGGESTIONS = [
  { name: "Netflix", category: "Streaming", monthly_cost: 15.49, usage_per_week: 3 },
  { name: "Spotify", category: "Music", monthly_cost: 9.99, usage_per_week: 5 },
  { name: "Gym Membership", category: "Fitness", monthly_cost: 40, usage_per_week: 1 },
  { name: "Amazon Prime", category: "Shopping", monthly_cost: 14.99, usage_per_week: 2 },
  { name: "Hulu", category: "Streaming", monthly_cost: 17.99, usage_per_week: 1 },
];

const DEBT_SUGGESTIONS: Debt[] = [
  { name: "Credit Card 1", balance: 6500, apr: 22.9, min_payment: 180 },
  { name: "Car Loan", balance: 12000, apr: 6.4, min_payment: 340 },
  { name: "Student Loan", balance: 24000, apr: 5.1, min_payment: 260 },
];

const GOAL_SUGGESTIONS: SavingsGoal[] = [
  { name: "Emergency Fund", target_amount: 15000, current_amount: 4000, priority: 1, deadline_months: 18 },
  { name: "Vacation", target_amount: 3500, current_amount: 700, priority: 3, deadline_months: 8 },
  { name: "House Down Payment", target_amount: 60000, current_amount: 12000, priority: 2, deadline_months: 48 },
];

const TXN_SUGGESTIONS: Txn[] = [
  { merchant: "Netflix", amount: 15.49, category: "Streaming", account: "Visa", date: "2026-04-03" },
  { merchant: "Netflix", amount: 15.49, category: "Streaming", account: "Visa", date: "2026-03-03" },
  { merchant: "Trader Joe's", amount: 126.2, category: "Groceries", account: "Checking", date: "2026-04-10" },
  { merchant: "Shell", amount: 62.88, category: "Transportation", account: "Visa", date: "2026-04-08" },
  { merchant: "Planet Fitness", amount: 24.99, category: "Fitness", account: "Checking", date: "2026-04-05" },
  { merchant: "Planet Fitness", amount: 24.99, category: "Fitness", account: "Checking", date: "2026-03-05" },
];

const HOUSEHOLD_MEMBER_SUGGESTIONS: HouseholdMember[] = [
  { name: "Alex" },
  { name: "Taylor" },
];

const SHARED_CATEGORY_SUGGESTIONS: SharedCategory[] = [
  { name: "Groceries", limit: 700 },
  { name: "Dining", limit: 250 },
  { name: "Utilities", limit: 350 },
  { name: "Kids", limit: 300 },
];

const SHARED_TXN_SUGGESTIONS: SharedTxn[] = [
  { member: "Alex", category: "Groceries", amount: 162, date: "2026-04-11", note: "Costco" },
  { member: "Taylor", category: "Dining", amount: 48, date: "2026-04-12", note: "Friday dinner" },
  { member: "Alex", category: "Utilities", amount: 121, date: "2026-04-03", note: "Electric" },
  { member: "Taylor", category: "Kids", amount: 220, date: "2026-04-17", note: "School supplies" },
];

// ─── Small reusable components ────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    on_track: "bg-emerald-100 text-emerald-700",
    balanced: "bg-emerald-100 text-emerald-700",
    ok: "bg-emerald-100 text-emerald-700",
    excellent: "bg-emerald-100 text-emerald-700",
    strong: "bg-emerald-100 text-emerald-700",
    keep: "bg-emerald-100 text-emerald-700",
    high_value: "bg-emerald-100 text-emerald-700",
    needs_attention: "bg-amber-100 text-amber-700",
    nearly_full: "bg-amber-100 text-amber-700",
    halfway: "bg-amber-100 text-amber-700",
    fair: "bg-amber-100 text-amber-700",
    medium_value: "bg-amber-100 text-amber-700",
    warning: "bg-amber-100 text-amber-700",
    connected: "bg-emerald-100 text-emerald-700",
    not_connected: "bg-red-100 text-red-700",
    low: "bg-amber-100 text-amber-700",
    over: "bg-red-100 text-red-700",
    overspent: "bg-red-100 text-red-700",
    over_budget: "bg-red-100 text-red-700",
    over_allocated: "bg-red-100 text-red-700",
    under: "bg-red-100 text-red-700",
    behind: "bg-red-100 text-red-700",
    critical: "bg-red-100 text-red-700",
    envelopes_overspent: "bg-red-100 text-red-700",
    needs_work: "bg-red-100 text-red-700",
    low_value: "bg-red-100 text-red-700",
    achieved: "bg-emerald-100 text-emerald-700",
  };
  const cls = map[status?.toLowerCase()] || "bg-gray-100 text-gray-700";
  return (
    <span className={"inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize " + cls}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function MetricCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <p className={"text-2xl font-bold " + (color || "text-gray-900")}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function ProgressBar({ value, max, color = "bg-indigo-500" }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div className={color + " h-2 rounded-full transition-all"} style={{ width: pct + "%" }} />
    </div>
  );
}

function RecommendationList({ recs }: { recs: string[] }) {
  if (!recs?.length) return null;
  return (
    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5">
      <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
        <span>💡</span> Recommendations
      </h4>
      <ul className="space-y-2">
        {recs.map((r, i) => (
          <li key={i} className="text-sm text-indigo-800 leading-relaxed flex gap-2">
            <span className="mt-0.5 shrink-0">→</span>
            <span>{r}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Result renderers ────────────────────────────────────────────────────
function ZeroBasedResults({ data }: { data: any }) {
  const s = data.zero_based_summary;
  const f = data.fifty_thirty_twenty;
  const breakdown: any[] = data.expense_breakdown || [];

  const fmtMoney = (n: number) => n < 0
    ? "-$" + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 })
    : "$" + n.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      {/* Key metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Monthly Income" value={fmtMoney(s.monthly_income)} />
        <MetricCard label="Total Expenses" value={fmtMoney(s.total_expenses)} color="text-red-600" />
        <MetricCard label="Savings Allocated" value={fmtMoney(s.savings_allocated)} color="text-emerald-600" />
        <MetricCard
          label="Unallocated"
          value={fmtMoney(s.unallocated_dollars)}
          color={s.unallocated_dollars >= 0 ? "text-emerald-600" : "text-red-600"}
          sub={s.unallocated_dollars >= 0 ? "Ready to assign" : "Overspent!"}
        />
      </div>

      {/* Status banner */}
      <div className={`rounded-xl p-4 flex items-center gap-3 ${s.status === "balanced" ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
        <span className="text-2xl">{s.status === "balanced" ? "✅" : "⚠️"}</span>
        <div>
          <p className={`font-semibold ${s.status === "balanced" ? "text-emerald-800" : "text-red-800"}`}>
            {s.status === "balanced" ? "Budget is Balanced — every dollar has a job!" : `Budget is Overspent by ${fmtMoney(Math.abs(s.unallocated_dollars))}`}
          </p>
          <p className="text-sm text-gray-600">
            {s.status === "balanced"
              ? `You have ${fmtMoney(s.unallocated_dollars)} still to assign — put it toward savings, investing, or debt.`
              : "Reduce expenses or allocate less to savings until your budget zeros out."}
          </p>
        </div>
      </div>

      {/* 50/30/20 */}
      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
          <h3 className="font-semibold text-gray-800">50 / 30 / 20 Rule Check</h3>
          <p className="text-xs text-gray-500 mt-0.5">Recommended: 50% Needs · 30% Wants · 20% Savings</p>
        </div>
        <div className="p-5 space-y-4">
          {[
            { label: "Needs (Housing, Food, Transport…)", actual: f.needs_actual, rec: f.needs_recommended, status: f.needs_status, color: "bg-blue-500" },
            { label: "Wants (Dining, Entertainment, Hobbies…)", actual: f.wants_actual, rec: f.wants_recommended, status: f.wants_status, color: "bg-purple-500" },
            { label: "Savings / Investments", actual: f.savings_actual, rec: f.savings_recommended, status: f.savings_status, color: "bg-emerald-500" },
          ].map((row) => (
            <div key={row.label}>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-gray-700">{row.label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-gray-900">{fmtMoney(row.actual)}</span>
                  <span className="text-xs text-gray-400">/ {fmtMoney(row.rec)} rec</span>
                  <StatusBadge status={row.status} />
                </div>
              </div>
              <ProgressBar value={row.actual} max={row.rec * 1.4} color={row.status === "on_track" ? row.color : "bg-red-400"} />
            </div>
          ))}
        </div>
      </div>

      {/* Expense breakdown table */}
      {breakdown.length > 0 && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
            <h3 className="font-semibold text-gray-800">Expense Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  {["Category", "Monthly Amount", "% of Income", "Status"].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {breakdown.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                    <td className="px-5 py-3 font-medium text-gray-800">{row.category}</td>
                    <td className="px-5 py-3 font-bold text-gray-900">{fmtMoney(row.amount)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{row.pct_of_income}%</span>
                        <div className="w-16"><ProgressBar value={row.pct_of_income} max={40} /></div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <StatusBadge status={row.over_budget ? "over" : "on_track"} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <RecommendationList recs={data.recommendations} />
    </div>
  );
}

function EnvelopeResults({ data }: { data: any }) {
  const s = data.envelope_summary;
  const envelopes: any[] = data.envelopes || [];
  const fmtMoney = (n: number) => "$" + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Income" value={"$" + s.monthly_income.toLocaleString()} />
        <MetricCard label="Total Allocated" value={"$" + s.total_allocated.toLocaleString()} />
        <MetricCard label="Total Spent" value={"$" + s.total_spent.toLocaleString()} color="text-red-600" />
        <MetricCard label="Unallocated" value={"$" + s.unallocated.toLocaleString()} color={s.unallocated >= 0 ? "text-emerald-600" : "text-red-600"} />
      </div>

      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3 flex justify-between items-center">
          <h3 className="font-semibold text-gray-800">Envelope Status</h3>
          <StatusBadge status={s.overall_status} />
        </div>
        <div className="divide-y divide-gray-100">
          {envelopes.map((env, i) => (
            <div key={i} className="px-5 py-4">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-800">{env.envelope}</span>
                  <StatusBadge status={env.status} />
                </div>
                <div className="text-right">
                  <span className="font-bold text-gray-900">{fmtMoney(env.spent)}</span>
                  <span className="text-gray-400 text-sm"> / {fmtMoney(env.allocated)}</span>
                </div>
              </div>
              <ProgressBar
                value={env.spent}
                max={env.allocated}
                color={env.status === "over_budget" ? "bg-red-500" : env.status === "nearly_full" ? "bg-amber-500" : "bg-emerald-500"}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{env.utilization_pct}% used</span>
                <span className={env.remaining >= 0 ? "text-emerald-600" : "text-red-600"}>
                  {env.remaining >= 0 ? fmtMoney(env.remaining) + " left" : fmtMoney(Math.abs(env.remaining)) + " over"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <RecommendationList recs={data.recommendations} />
    </div>
  );
}

function SubscriptionResults({ data }: { data: any }) {
  const s = data.subscription_summary;
  const subs: any[] = data.subscriptions || [];
  const cancelCandidates: string[] = data.cancel_candidates || [];

  const colorMap: Record<string, string> = {
    high_value: "text-emerald-600",
    medium_value: "text-amber-600",
    low_value: "text-red-600",
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Total Subscriptions" value={String(s.total_subscriptions)} />
        <MetricCard label="Monthly Cost" value={"$" + s.monthly_total.toFixed(2)} color="text-red-600" />
        <MetricCard label="Annual Cost" value={"$" + s.annual_total.toLocaleString()} color="text-red-600" />
        <MetricCard label="Potential Savings/mo" value={"$" + s.potential_monthly_savings.toFixed(2)} color="text-emerald-600" sub={"$" + s.potential_annual_savings + "/yr"} />
      </div>

      {cancelCandidates.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
          <span className="text-2xl">✂️</span>
          <div>
            <p className="font-semibold text-red-800">Cancel these to save ${s.potential_monthly_savings}/mo</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {cancelCandidates.map((name, i) => (
                <span key={i} className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">{name}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
          <h3 className="font-semibold text-gray-800">All Subscriptions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {["Service", "Category", "Monthly", "Annual", "Usage/week", "Value", "Action"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {subs.map((sub, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                  <td className="px-4 py-3 font-semibold text-gray-800">{sub.name}</td>
                  <td className="px-4 py-3 text-gray-600">{sub.category}</td>
                  <td className="px-4 py-3 font-bold text-gray-900">${sub.monthly_cost.toFixed(2)}</td>
                  <td className="px-4 py-3 text-gray-600">${sub.annual_cost.toFixed(2)}</td>
                  <td className="px-4 py-3 text-gray-600">{sub.usage_per_week}x</td>
                  <td className="px-4 py-3">
                    <span className={"font-semibold " + (colorMap[sub.value_rating] || "text-gray-600")}>
                      {sub.value_rating.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={
                      sub.recommendation === "Consider canceling"
                        ? "bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-semibold"
                        : sub.recommendation === "Review usage"
                        ? "bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-semibold"
                        : "bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-semibold"
                    }>
                      {sub.recommendation}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <RecommendationList recs={data.recommendations} />
    </div>
  );
}

function ForecastResults({ data }: { data: any }) {
  const s = data.forecast_summary;
  const scenarios = data.scenarios || {};
  const months: any[] = scenarios.base || [];
  const optimistic: any[] = scenarios.optimistic || [];
  const pessimistic: any[] = scenarios.pessimistic || [];
  const fmtMoney = (n: number) => {
    if (n === null || n === undefined) return "—";
    const abs = Math.abs(n);
    const str = abs >= 1000 ? abs.toLocaleString(undefined, { maximumFractionDigits: 0 }) : abs.toFixed(2);
    return (n < 0 ? "-$" : "$") + str;
  };

  const balances = months.map(m => m.projected_balance);
  const maxBalance = balances.length ? Math.max(...balances) : 0;
  const minBalance = balances.length ? Math.min(...balances) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard label="Starting Balance" value={fmtMoney(s.starting_balance)} />
        <MetricCard label="Monthly Net Cash Flow" value={fmtMoney(s.monthly_net_cashflow)} color={s.monthly_net_cashflow >= 0 ? "text-emerald-600" : "text-red-600"} />
        <MetricCard label="Savings Rate" value={s.savings_rate_pct + "%"} color="text-indigo-600" />
        <MetricCard label="Year-End Balance" value={fmtMoney(s.projected_year_end_balance)} color="text-emerald-600" />
        <MetricCard label="Optimistic / Pessimistic" value={`${fmtMoney(s.optimistic_year_end)} / ${fmtMoney(s.pessimistic_year_end)}`} />
        <MetricCard label="Inflation" value={`${s.inflation_rate_pct}%`} sub={s.months_to_double ? `Months to double: ${s.months_to_double}` : "Months to double: N/A"} />
      </div>

      {/* Sparkline-style visual bar chart */}
      <div className="rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-800 mb-4">Projected Balance — 12 Months</h3>
        <div className="flex items-end gap-1 h-32">
          {months.map((m, i) => {
            const range = maxBalance - minBalance || 1;
            const heightPct = ((m.projected_balance - minBalance) / range) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-indigo-500 rounded-t-sm transition-all"
                  style={{ height: Math.max(4, heightPct) + "%" }}
                  title={m.month + ": " + fmtMoney(m.projected_balance)}
                />
                <span className="text-[10px] text-gray-400">{m.month}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
          <h3 className="font-semibold text-gray-800">Scenario Comparison (Year-End)</h3>
        </div>
        <div className="p-5 grid sm:grid-cols-3 gap-3">
          <MetricCard label="Base" value={fmtMoney(months[11]?.projected_balance ?? s.projected_year_end_balance)} color="text-indigo-600" />
          <MetricCard label="Optimistic" value={fmtMoney(optimistic[11]?.projected_balance ?? s.optimistic_year_end)} color="text-emerald-600" sub="+5% income, -10% expenses" />
          <MetricCard label="Pessimistic" value={fmtMoney(pessimistic[11]?.projected_balance ?? s.pessimistic_year_end)} color="text-red-600" sub="-5% income, +10% expenses" />
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
          <h3 className="font-semibold text-gray-800">Monthly Cash Flow Detail</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {["Month", "Income", "Expenses", "Debt Payments", "Investment Return", "Net Cash Flow", "Projected Balance"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {months.map((m, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                  <td className="px-4 py-2.5 font-semibold text-gray-700">{m.month}</td>
                  <td className="px-4 py-2.5 text-emerald-700 font-medium">{fmtMoney(m.income)}</td>
                  <td className="px-4 py-2.5 text-red-600 font-medium">{fmtMoney(m.expenses)}</td>
                  <td className="px-4 py-2.5 text-red-500">{fmtMoney(m.debt)}</td>
                  <td className="px-4 py-2.5 text-indigo-600">{fmtMoney(m.investment_return)}</td>
                  <td className={"px-4 py-2.5 font-bold " + (m.net_cashflow >= 0 ? "text-emerald-700" : "text-red-700")}>{fmtMoney(m.net_cashflow)}</td>
                  <td className="px-4 py-2.5 font-bold text-gray-900">{fmtMoney(m.projected_balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function NetWorthResults({ data }: { data: any }) {
  const s = data.net_worth_summary;
  const assets: any[] = data.assets || [];
  const liabilities: any[] = data.liabilities || [];
  const fmtMoney = (n: number) => (n < 0 ? "-$" : "$") + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Total Assets" value={fmtMoney(s.total_assets)} color="text-emerald-600" />
        <MetricCard label="Total Liabilities" value={fmtMoney(s.total_liabilities)} color="text-red-600" />
        <MetricCard label="Net Worth" value={fmtMoney(s.net_worth)} color={s.net_worth >= 0 ? "text-indigo-600" : "text-red-600"} />
        <MetricCard label="Debt-to-Asset Ratio" value={s.debt_to_asset_ratio_pct + "%"} sub={<StatusBadge status={s.financial_health} /> as any} />
      </div>

      {/* Net worth visual */}
      <div className="rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-800 mb-3">Assets vs Liabilities</h3>
        <div className="space-y-2">
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="font-medium text-emerald-700">Assets</span>
              <span className="font-bold">{fmtMoney(s.total_assets)}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden">
              <div className="bg-emerald-500 h-4 rounded-full" style={{ width: "100%" }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="font-medium text-red-600">Liabilities</span>
              <span className="font-bold">{fmtMoney(s.total_liabilities)}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden">
              <div
                className="bg-red-500 h-4 rounded-full"
                style={{ width: s.total_assets > 0 ? Math.min(100, (s.total_liabilities / s.total_assets) * 100) + "%" : "0%" }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {/* Assets table */}
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-emerald-50 border-b border-gray-200 px-5 py-3">
            <h3 className="font-semibold text-emerald-800">Assets</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-100">
                {["Asset", "Type", "Value", "% of Total"].map(h => <th key={h} className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>)}
              </tr></thead>
              <tbody>
                {assets.map((a, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{a.name}</td>
                    <td className="px-4 py-2.5 text-gray-500">{a.type}</td>
                    <td className="px-4 py-2.5 font-bold text-emerald-700">{fmtMoney(a.value)}</td>
                    <td className="px-4 py-2.5 text-gray-600">{a.pct_of_assets}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* Liabilities table */}
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-red-50 border-b border-gray-200 px-5 py-3">
            <h3 className="font-semibold text-red-800">Liabilities</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b border-gray-100">
                {["Liability", "Type", "Balance", "Interest Rate"].map(h => <th key={h} className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>)}
              </tr></thead>
              <tbody>
                {liabilities.map((l, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{l.name}</td>
                    <td className="px-4 py-2.5 text-gray-500">{l.type}</td>
                    <td className="px-4 py-2.5 font-bold text-red-600">{fmtMoney(l.balance)}</td>
                    <td className="px-4 py-2.5 font-medium" style={{ color: l.interest_rate > 8 ? "#dc2626" : "#374151" }}>
                      {l.interest_rate}%{l.interest_rate > 8 ? " ⚠️" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <RecommendationList recs={data.recommendations} />
    </div>
  );
}

function DebtPayoffResults({ data }: { data: any }) {
  const summary = data.debt_summary;
  const snowball = data.snowball;
  const avalanche = data.avalanche;
  const recs = data.recommendations || [];
  const fmtMoney = (n: number) => (n < 0 ? "-$" : "$") + Math.abs(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Total Debt" value={fmtMoney(summary.total_debt)} color="text-red-600" />
        <MetricCard label="Monthly Payment" value={fmtMoney(summary.total_monthly_payment)} />
        <MetricCard label="Debts" value={String(summary.number_of_debts)} />
        <MetricCard label="Avalanche Interest Saved" value={fmtMoney(data.interest_saved_by_avalanche)} color="text-emerald-600" />
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {[snowball, avalanche].map((strategy: any) => (
          <div key={strategy.strategy} className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
              <h3 className="font-semibold text-gray-800">{strategy.strategy} Strategy</h3>
            </div>
            <div className="p-5 space-y-3">
              <MetricCard label="Debt Free In" value={`${strategy.months_to_freedom} months`} />
              <MetricCard label="Total Interest" value={fmtMoney(strategy.total_interest_paid)} color="text-red-600" />
              <MetricCard label="Debt Free Date" value={strategy.debt_free_date} />
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Payoff Order</p>
                <div className="flex flex-wrap gap-2">
                  {(strategy.payoff_order || []).map((name: string, i: number) => (
                    <span key={i} className="px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium">{i + 1}. {name}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <RecommendationList recs={recs} />
    </div>
  );
}

function RetirementResults({ data }: { data: any }) {
  const s = data.retirement_summary;
  const yearly = data.yearly_projection || [];
  const fmtMoney = (n: number) => (n < 0 ? "-$" : "$") + Math.abs(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard label="FIRE Number" value={fmtMoney(s.fire_number)} />
        <MetricCard label="Inflation-Adjusted FIRE" value={fmtMoney(s.inflation_adjusted_fire_number)} />
        <MetricCard label="Current Progress" value={`${s.current_progress_pct}%`} color="text-indigo-600" />
        <MetricCard label="Projected at Retirement" value={fmtMoney(s.projected_at_retirement)} color={s.retirement_ready ? "text-emerald-600" : "text-red-600"} />
        <MetricCard label="Surplus / Shortfall" value={fmtMoney(s.surplus_or_shortfall)} color={s.surplus_or_shortfall >= 0 ? "text-emerald-600" : "text-red-600"} />
        <MetricCard label="Estimated FIRE Age" value={s.fire_age ? String(s.fire_age) : "N/A"} />
      </div>

      <div className="rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-800 mb-3">Progress To FIRE</h3>
        <ProgressBar value={s.current_savings} max={s.fire_number || 1} color="bg-orange-500" />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{fmtMoney(s.current_savings)} today</span>
          <span>{fmtMoney(s.fire_number)} target</span>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
          <h3 className="font-semibold text-gray-800">Yearly Projection</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {["Age", "Year", "Projected Savings", "% to FIRE", "Status"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {yearly.slice(0, 20).map((row: any, i: number) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                  <td className="px-4 py-2.5 font-medium text-gray-700">{row.age}</td>
                  <td className="px-4 py-2.5 text-gray-600">{row.year}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-900">{fmtMoney(row.projected_savings)}</td>
                  <td className="px-4 py-2.5 text-indigo-700 font-medium">{row.pct_to_fire}%</td>
                  <td className="px-4 py-2.5"><StatusBadge status={row.on_track ? "on_track" : "needs_work"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <RecommendationList recs={data.recommendations || []} />
    </div>
  );
}

function SavingsGoalsResults({ data }: { data: any }) {
  const s = data.goals_summary;
  const goals = data.goals || [];
  const fmtMoney = (n: number) => "$" + Math.abs(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Goals" value={String(s.total_goals)} />
        <MetricCard label="Total Still Needed" value={fmtMoney(s.total_still_needed)} color="text-red-600" />
        <MetricCard label="Monthly Available" value={fmtMoney(s.monthly_available)} color="text-indigo-600" />
        <MetricCard label="Achieved" value={String(s.achieved)} color="text-emerald-600" />
      </div>

      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
          <h3 className="font-semibold text-gray-800">Goal Progress</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {goals.map((goal: any, i: number) => (
            <div key={i} className="px-5 py-4 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-800">{goal.name}</p>
                  <StatusBadge status={goal.status} />
                </div>
                <p className="text-sm text-gray-600">Priority {goal.priority}</p>
              </div>
              <ProgressBar value={goal.current_amount} max={goal.target_amount || 1} color="bg-indigo-500" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-600">
                <p>Saved: <span className="font-semibold">{fmtMoney(goal.current_amount)}</span></p>
                <p>Target: <span className="font-semibold">{fmtMoney(goal.target_amount)}</span></p>
                <p>Needed/mo: <span className="font-semibold">{fmtMoney(goal.monthly_required)}</span></p>
                <p>ETA: <span className="font-semibold">{goal.projected_reach_date || "N/A"}</span></p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <RecommendationList recs={data.recommendations || []} />
    </div>
  );
}

function EmergencyFundResults({ data }: { data: any }) {
  const s = data.emergency_fund_summary;
  const fmtMoney = (n: number) => "$" + Math.abs(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Current Fund" value={fmtMoney(s.current_fund)} />
        <MetricCard label="Coverage" value={`${s.coverage_months} months`} color={s.coverage_months >= 3 ? "text-emerald-600" : "text-red-600"} />
        <MetricCard label="3-Month Target" value={fmtMoney(s.target_3_months)} />
        <MetricCard label="6-Month Target" value={fmtMoney(s.target_6_months)} />
      </div>

      <div className="rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800">Emergency Readiness</h3>
          <StatusBadge status={s.status} />
        </div>
        <ProgressBar value={s.current_fund} max={s.recommended_target || 1} color="bg-teal-500" />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{fmtMoney(s.current_fund)} now</span>
          <span>{fmtMoney(s.recommended_target)} recommended</span>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Shortfall to 3 months</p>
          <p className="text-xl font-bold text-red-600">{fmtMoney(s.shortfall_to_3mo)}</p>
          <p className="text-xs text-gray-500 mt-1">Time to hit target: {s.months_to_3mo_target ?? "N/A"} months</p>
        </div>
        <div className="rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Shortfall to 6 months</p>
          <p className="text-xl font-bold text-red-600">{fmtMoney(s.shortfall_to_6mo)}</p>
          <p className="text-xs text-gray-500 mt-1">Time to hit target: {s.months_to_6mo_target ?? "N/A"} months</p>
        </div>
      </div>

      <RecommendationList recs={data.recommendations || []} />
    </div>
  );
}

function TransactionSyncResults({ data }: { data: any }) {
  const s = data.sync_summary;
  const recurring = data.recurring_charges || [];
  const categorySpend = data.category_spend || [];
  const fmtMoney = (n: number) => "$" + Math.abs(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Connected Accounts" value={String(s.connected_accounts)} />
        <MetricCard label="Transactions Analyzed" value={String(s.transactions_analyzed)} />
        <MetricCard label="Recurring Patterns" value={String(s.recurring_patterns_detected)} color="text-indigo-600" />
        <MetricCard label="Total Spend Analyzed" value={fmtMoney(s.total_spend_analyzed)} color="text-red-600" />
      </div>

      <div className="rounded-xl border border-gray-200 p-5 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-800">Sync Status</h3>
          <p className="text-sm text-gray-500">Automated aggregation readiness for accounts and recurring spend.</p>
        </div>
        <StatusBadge status={s.sync_status} />
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
            <h3 className="font-semibold text-gray-800">Recurring Charges</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {recurring.length === 0 && <p className="px-5 py-4 text-sm text-gray-500">No recurring patterns detected yet.</p>}
            {recurring.map((r: any, i: number) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-800 capitalize">{r.merchant}</p>
                  <p className="text-xs text-gray-500">{r.occurrences} occurrences</p>
                </div>
                <p className="font-semibold text-red-600">{fmtMoney(r.estimated_monthly)}/mo</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
            <h3 className="font-semibold text-gray-800">Top Categories</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {categorySpend.slice(0, 8).map((c: any, i: number) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between">
                <p className="font-medium text-gray-700">{c.category}</p>
                <p className="font-semibold text-gray-900">{fmtMoney(c.amount)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <RecommendationList recs={data.recommendations || []} />
    </div>
  );
}

function HouseholdSyncResults({ data }: { data: any }) {
  const s = data.household_summary;
  const categories = data.category_status || [];
  const members = data.member_spend || [];
  const alerts = data.large_purchase_alerts || [];
  const fmtMoney = (n: number) => "$" + Math.abs(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard label="Household Members" value={String(s.household_members)} />
        <MetricCard label="Budgeted" value={fmtMoney(s.total_budgeted)} />
        <MetricCard label="Spent" value={fmtMoney(s.total_spent)} color="text-red-600" />
        <MetricCard label="Remaining" value={fmtMoney(s.remaining_budget)} color={s.remaining_budget >= 0 ? "text-emerald-600" : "text-red-600"} />
      </div>

      <div className="rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
          <h3 className="font-semibold text-gray-800">Shared Category Controls</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {categories.map((c: any, i: number) => (
            <div key={i} className="px-5 py-4 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="font-semibold text-gray-800">{c.category}</p>
                <StatusBadge status={c.status} />
              </div>
              <ProgressBar value={c.spent} max={c.limit || 1} color={c.status === "over_budget" ? "bg-red-500" : c.status === "warning" ? "bg-amber-500" : "bg-emerald-500"} />
              <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                <p>Limit: <span className="font-semibold">{fmtMoney(c.limit)}</span></p>
                <p>Spent: <span className="font-semibold">{fmtMoney(c.spent)}</span></p>
                <p>Remaining: <span className="font-semibold">{fmtMoney(c.remaining)}</span></p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-800 mb-3">Member Spend</h3>
          <div className="space-y-2">
            {members.map((m: any, i: number) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-gray-700">{m.member} ({m.transactions} txns)</span>
                <span className="font-semibold text-gray-900">{fmtMoney(m.spend)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-800 mb-3">Large Purchase Alerts</h3>
          {alerts.length === 0 && <p className="text-sm text-gray-500">No large purchases exceeded threshold.</p>}
          <div className="space-y-2">
            {alerts.slice(0, 6).map((a: any, i: number) => (
              <div key={i} className="text-sm border border-red-100 bg-red-50 rounded-lg px-3 py-2 flex justify-between">
                <span className="text-red-700">{a.member} · {a.category}</span>
                <span className="font-semibold text-red-800">{fmtMoney(a.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <RecommendationList recs={data.recommendations || []} />
    </div>
  );
}

// ─── Form sections ────────────────────────────────────────────────────────
function DynList<T>({
  items, setItems, empty, renderRow, addLabel
}: {
  items: T[]; setItems: (items: T[]) => void; empty: T;
  renderRow: (item: T, idx: number, update: (idx: number, val: T) => void, remove: (idx: number) => void) => React.ReactNode;
  addLabel: string;
}) {
  const update = (idx: number, val: T) => setItems(items.map((it, i) => (i === idx ? val : it)));
  const remove = (idx: number) => setItems(items.filter((_, i) => i !== idx));
  const add = () => setItems([...items, { ...empty as any }]);
  return (
    <div className="space-y-2">
      {items.map((item, idx) => renderRow(item, idx, update, remove))}
      <button type="button" onClick={add}
        className="mt-1 px-4 py-2 rounded-lg border border-dashed border-indigo-300 text-indigo-600 text-sm font-medium hover:bg-indigo-50 transition w-full">
        + {addLabel}
      </button>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────
export default function BudgetPlanner() {
  const [mode, setMode] = useState<Mode>("zero_based");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Form state per mode
  const [income, setIncome] = useState(5000);
  const [savingsGoalPct, setSavingsGoalPct] = useState(20);
  const [debtMonthly, setDebtMonthly] = useState(0);
  const [expenses, setExpenses] = useState<Expense[]>([
    { category: "Housing / Rent", amount: 1500, suggested_pct: 30 },
    { category: "Groceries", amount: 400, suggested_pct: 10 },
    { category: "Transportation", amount: 300, suggested_pct: 10 },
    { category: "Utilities", amount: 150, suggested_pct: 5 },
    { category: "Dining Out", amount: 200, suggested_pct: 5 },
  ]);
  const [envelopes, setEnvelopes] = useState<Envelope[]>([
    { name: "Groceries", allocated: 500, spent: 320 },
    { name: "Dining Out", allocated: 200, spent: 180 },
    { name: "Entertainment", allocated: 150, spent: 60 },
    { name: "Emergency Fund", allocated: 300, spent: 0 },
  ]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([
    { name: "Netflix", monthly_cost: 15.49, category: "Streaming", usage_per_week: 3 },
    { name: "Spotify", monthly_cost: 9.99, category: "Music", usage_per_week: 5 },
    { name: "Gym Membership", monthly_cost: 40, category: "Fitness", usage_per_week: 1 },
    { name: "Hulu", monthly_cost: 17.99, category: "Streaming", usage_per_week: 0 },
    { name: "Cloud Storage", monthly_cost: 2.99, category: "Tech", usage_per_week: 7 },
  ]);
  const [currentSavings, setCurrentSavings] = useState(10000);
  const [growthRate, setGrowthRate] = useState(0.6);
  const [inflationRate, setInflationRate] = useState(3);
  const [assets, setAssets] = useState<Asset[]>([
    { name: "Checking Account", value: 5000, type: "Cash" },
    { name: "401(k)", value: 35000, type: "Retirement" },
    { name: "Car", value: 15000, type: "Vehicle" },
  ]);
  const [liabilities, setLiabilities] = useState<Liability[]>([
    { name: "Car Loan", balance: 8000, interest_rate: 5.9, type: "Auto" },
    { name: "Credit Card", balance: 2500, interest_rate: 22.9, type: "Credit Card" },
  ]);
  const [debts, setDebts] = useState<Debt[]>(DEBT_SUGGESTIONS);
  const [extraDebtPayment, setExtraDebtPayment] = useState(300);
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [annualReturnPct, setAnnualReturnPct] = useState(7);
  const [withdrawalRatePct, setWithdrawalRatePct] = useState(4);
  const [annualRetirementExpenses, setAnnualRetirementExpenses] = useState(60000);
  const [monthlyContribution, setMonthlyContribution] = useState(800);
  const [goals, setGoals] = useState<SavingsGoal[]>(GOAL_SUGGESTIONS);
  const [monthlySavingsAvailable, setMonthlySavingsAvailable] = useState(1200);
  const [emergencyCurrent, setEmergencyCurrent] = useState(4000);
  const [monthlyExpenses, setMonthlyExpenses] = useState(3500);
  const [jobSector, setJobSector] = useState("employed");
  const [connectedAccounts, setConnectedAccounts] = useState<{ name: string; type: string }[]>([
    { name: "Checking", type: "bank" },
    { name: "Visa", type: "credit" },
  ]);
  const [transactions, setTransactions] = useState<Txn[]>(TXN_SUGGESTIONS);
  const [householdMembers, setHouseholdMembers] = useState<HouseholdMember[]>(HOUSEHOLD_MEMBER_SUGGESTIONS);
  const [sharedCategories, setSharedCategories] = useState<SharedCategory[]>(SHARED_CATEGORY_SUGGESTIONS);
  const [sharedTransactions, setSharedTransactions] = useState<SharedTxn[]>(SHARED_TXN_SUGGESTIONS);
  const [largePurchaseThreshold, setLargePurchaseThreshold] = useState(150);
  const [workspaceBusy, setWorkspaceBusy] = useState(false);
  const [workspaceNotice, setWorkspaceNotice] = useState<{ type: "success" | "info" | "error"; message: string } | null>(null);
  const [householdId, setHouseholdId] = useState<string | null>(null);
  const [householdName, setHouseholdName] = useState("My Household");
  const [householdNotifications, setHouseholdNotifications] = useState<any[]>([]);
  const [navExpanded, setNavExpanded] = useState<boolean>(() => localStorage.getItem("bp-nav-expanded") !== "false");

  const resultsRef = React.useRef<HTMLDivElement>(null);
  const noticeTimerRef = React.useRef<number | null>(null);

  const showWorkspaceNotice = (type: "success" | "info" | "error", message: string) => {
    if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    setWorkspaceNotice({ type, message });
    noticeTimerRef.current = window.setTimeout(() => setWorkspaceNotice(null), 2600);
  };

  const loadTransactionWorkspace = async () => {
    setWorkspaceBusy(true);
    setError(null);
    try {
      const data = await api.getBudgetTransactionSyncData();
      if (Array.isArray(data.accounts) && data.accounts.length) {
        setConnectedAccounts(data.accounts.map((a: any) => ({ name: a.account_name || a.name, type: a.account_type || a.type || "bank" })));
      }
      if (Array.isArray(data.transactions) && data.transactions.length) {
        setTransactions(data.transactions.map((t: any) => ({
          merchant: t.merchant,
          amount: Number(t.amount || 0),
          category: t.category || "Other",
          account: t.account_name || t.account || "Checking",
          date: t.txn_date || new Date().toISOString().slice(0, 10),
        })));
      }
      showWorkspaceNotice("info", "Transaction workspace refreshed");
    } catch (e: any) {
      setError(e.message || "Failed to load transaction workspace");
      showWorkspaceNotice("error", e.message || "Failed to load transaction workspace");
    } finally {
      setWorkspaceBusy(false);
    }
  };

  const saveTransactionWorkspace = async () => {
    setWorkspaceBusy(true);
    setError(null);
    try {
      await api.saveBudgetConnectedAccounts(
        connectedAccounts.map((a) => ({
          account_name: a.name,
          account_type: a.type,
          provider: "manual",
        }))
      );
      await api.saveBudgetSyncedTransactions(
        transactions.map((t) => ({
          merchant: t.merchant,
          amount: t.amount,
          category: t.category,
          account: t.account,
          txn_date: t.date,
        }))
      );
      showWorkspaceNotice("success", "Transaction workspace saved");
    } catch (e: any) {
      setError(e.message || "Failed to save transaction workspace");
      showWorkspaceNotice("error", e.message || "Failed to save transaction workspace");
    } finally {
      setWorkspaceBusy(false);
    }
  };

  const loadHouseholdWorkspace = async (preferredId?: string) => {
    setWorkspaceBusy(true);
    setError(null);
    try {
      let id = preferredId || householdId;
      if (!id) {
        const list = await api.getBudgetHouseholds();
        id = list.households?.[0]?.id || null;
      }
      if (!id) {
        setWorkspaceBusy(false);
        return;
      }

      const detail = await api.getBudgetHouseholdDetail(id);
      setHouseholdId(id);
      setHouseholdName(detail.household?.name || householdName);
      setHouseholdMembers((detail.members || []).map((m: any) => ({ name: m.display_name })));
      setSharedCategories((detail.categories || []).map((c: any) => ({ name: c.name, limit: Number(c.monthly_limit || 0) })));
      setSharedTransactions((detail.transactions || []).map((t: any) => ({
        member: t.member_name || "",
        category: t.category_name || "Other",
        amount: Number(t.amount || 0),
        date: t.txn_date || new Date().toISOString().slice(0, 10),
        note: t.note || "",
      })));
      setHouseholdNotifications(detail.notifications || []);
      showWorkspaceNotice("info", "Household workspace refreshed");
    } catch (e: any) {
      setError(e.message || "Failed to load household workspace");
      showWorkspaceNotice("error", e.message || "Failed to load household workspace");
    } finally {
      setWorkspaceBusy(false);
    }
  };

  const saveHouseholdWorkspace = async () => {
    setWorkspaceBusy(true);
    setError(null);
    try {
      let id = householdId;
      if (!id) {
        const created = await api.createBudgetHousehold(householdName || "My Household");
        id = created.household_id;
        setHouseholdId(id);
      }
      const seenMemberNames = new Set<string>();
      const cleanMembers = householdMembers
        .map((m) => ({ display_name: String(m.name || "").trim() }))
        .filter((m) => {
          if (!m.display_name) return false;
          const key = m.display_name.toLowerCase();
          if (seenMemberNames.has(key)) return false;
          seenMemberNames.add(key);
          return true;
        });
      await api.saveBudgetHouseholdMembers(
        id,
        cleanMembers
      );
      await api.saveBudgetHouseholdCategories(
        id,
        sharedCategories.map((c) => ({ name: c.name, monthly_limit: c.limit }))
      );
      await api.saveBudgetHouseholdTransactions(
        id,
        sharedTransactions.map((t) => ({
          member: t.member,
          category: t.category,
          amount: t.amount,
          txn_date: t.date,
          note: t.note,
        })),
        largePurchaseThreshold
      );
      await loadHouseholdWorkspace(id);
      showWorkspaceNotice("success", "Household workspace saved");
    } catch (e: any) {
      setError(e.message || "Failed to save household workspace");
      showWorkspaceNotice("error", e.message || "Failed to save household workspace");
    } finally {
      setWorkspaceBusy(false);
    }
  };

  useEffect(() => {
    if (mode === "transaction_sync") {
      void loadTransactionWorkspace();
    }
    if (mode === "household_sync") {
      void loadHouseholdWorkspace();
    }
  }, [mode]);

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("bp-nav-expanded", String(navExpanded));
  }, [navExpanded]);

  const handleRun = async () => {
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const payload: Record<string, any> = {
        mode,
        monthly_income: income,
        savings_goal_pct: savingsGoalPct,
        debt_payment_monthly: debtMonthly,
      };
      if (mode === "zero_based") payload.expenses = expenses;
      if (mode === "envelope") payload.envelopes = envelopes;
      if (mode === "subscriptions") payload.subscriptions = subscriptions;
      if (mode === "forecast") {
        payload.expenses = expenses;
        payload.current_savings_balance = currentSavings;
        payload.monthly_investment_growth_rate = growthRate;
        payload.inflation_rate_pct = inflationRate;
      }
      if (mode === "net_worth") {
        payload.assets = assets;
        payload.liabilities = liabilities;
        payload.monthly_contribution = monthlyContribution;
      }
      if (mode === "debt_payoff") {
        payload.debts = debts;
        payload.extra_monthly_payment = extraDebtPayment;
      }
      if (mode === "retirement") {
        payload.current_age = currentAge;
        payload.target_retirement_age = retirementAge;
        payload.current_savings_balance = currentSavings;
        payload.monthly_contribution = monthlyContribution;
        payload.annual_return_pct = annualReturnPct;
        payload.annual_expenses_in_retirement = annualRetirementExpenses;
        payload.withdrawal_rate_pct = withdrawalRatePct;
        payload.inflation_rate_pct = inflationRate;
      }
      if (mode === "savings_goals") {
        payload.savings_goals = goals;
        payload.monthly_savings_available = monthlySavingsAvailable;
      }
      if (mode === "emergency_fund") {
        payload.monthly_expenses = monthlyExpenses;
        payload.emergency_fund_current = emergencyCurrent;
        payload.monthly_contribution = monthlyContribution;
        payload.job_sector = jobSector;
      }
      if (mode === "transaction_sync") {
        payload.connected_accounts = connectedAccounts;
        payload.transactions = transactions;
      }
      if (mode === "household_sync") {
        payload.household_members = householdMembers;
        payload.shared_categories = sharedCategories;
        payload.shared_transactions = sharedTransactions;
        payload.large_purchase_threshold = largePurchaseThreshold;
      }
      const res = await api.runTool("budget-planner", payload);
      setResult(res);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e: any) {
      setError(e.message || "Failed to run analysis");
    } finally {
      setRunning(false);
    }
  };

  const activeTab = TABS.find(t => t.mode === mode)!;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
          <div className="flex items-center gap-2 mb-3 text-sm text-gray-500">
            <Link to="/tools" className="hover:text-indigo-600 transition">Tools</Link>
            <span>/</span>
            <span className="text-gray-800 font-medium">Budget Planner</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wide">Finance</span>
            <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-3 py-1 rounded-full">Free Tool</span>
          </div>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-2">💰 Budget Planner</h1>
          <p className="text-gray-500 text-lg max-w-3xl">
            Your complete personal finance command center — budgeting, debt payoff, FIRE planning, savings goals, and emergency fund readiness.
          </p>
        </div>
      </div>

      {/* ── Mobile tab selector (hidden sm+) ── */}
      <div className="sm:hidden bg-white border-b border-gray-200 sticky top-[84px] z-40">
        <div className="overflow-x-auto flex gap-0 -mb-px px-4">
          {TABS.map(tab => (
            <button
              key={tab.mode}
              onClick={() => { setMode(tab.mode); setResult(null); setError(null); }}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition shrink-0 ${
                mode === tab.mode
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300"
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Desktop: docked sidebar + main content ── */}
      <div className="flex flex-row">
        {/* Docked left sidebar (sm+ only) */}
        <div
          className={`hidden sm:flex flex-col bg-white border-r border-gray-200 shrink-0 sticky top-[84px] overflow-y-auto transition-all duration-200 ${
            navExpanded ? "w-[264px]" : "w-[68px]"
          }`}
          style={{ maxHeight: "calc(100vh - 84px)" }}
        >
          {/* Collapse/expand toggle */}
          <button
            onClick={() => setNavExpanded(v => !v)}
            className="flex items-center justify-end px-3 h-10 border-b border-gray-100 hover:bg-gray-50 text-gray-400 hover:text-gray-700 text-xs font-semibold transition shrink-0"
            title={navExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            {navExpanded ? (
              <span className="flex items-center gap-1">Collapse <span>◀</span></span>
            ) : (
              <span>▶</span>
            )}
          </button>
          {/* Nav items */}
          {TABS.map(tab => (
            <button
              key={tab.mode}
              onClick={() => { setMode(tab.mode); setResult(null); setError(null); }}
              title={tab.label}
              className={`flex items-center gap-3 px-3 py-3 text-left w-full transition shrink-0 border-l-2 ${
                mode === tab.mode
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <span className="text-xl shrink-0">{tab.icon}</span>
              {navExpanded && (
                <div className="min-w-0 text-left">
                  <p className="text-sm font-semibold leading-tight truncate">{tab.label}</p>
                  <p className="text-xs text-gray-400 leading-tight truncate mt-0.5">{tab.desc}</p>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Main content area */}
        <div className="flex-1 min-w-0 px-3 sm:px-6 py-8">
          <div className="flex flex-col xl:flex-row gap-8 items-start">

          {/* ── Left: Form panel ── */}
          <div className="w-full xl:w-[40%] shrink-0">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden sticky top-[145px]">
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4">
                <h2 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
                  <span>{activeTab.icon}</span> {activeTab.label}
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">{activeTab.desc}</p>
              </div>
              <div className="p-6 space-y-5">
                {workspaceNotice && (
                  <div
                    className={
                      "rounded-xl px-3 py-2 text-xs font-medium border " +
                      (workspaceNotice.type === "success"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : workspaceNotice.type === "error"
                        ? "bg-red-50 text-red-700 border-red-200"
                        : "bg-blue-50 text-blue-700 border-blue-200")
                    }
                  >
                    {workspaceNotice.message}
                  </div>
                )}

                {/* Shared: income */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Monthly Take-Home Income ($)</label>
                  <input type="number" value={income} min={0} onChange={e => setIncome(Number(e.target.value))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                </div>

                {/* Zero-based & Forecast: expenses */}
                {(mode === "zero_based" || mode === "forecast") && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Savings Goal (%)</label>
                        <input type="number" value={savingsGoalPct} min={0} max={100} onChange={e => setSavingsGoalPct(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Monthly Debt Payments ($)</label>
                        <input type="number" value={debtMonthly} min={0} onChange={e => setDebtMonthly(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Monthly Expenses</label>
                      <DynList
                        items={expenses}
                        setItems={setExpenses}
                        empty={{ category: "", amount: 0, suggested_pct: 5 }}
                        addLabel="Add Expense"
                        renderRow={(item, idx, update, remove) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input
                              placeholder="Category"
                              value={item.category}
                              onChange={e => update(idx, { ...item, category: e.target.value })}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                            <input
                              type="number" placeholder="$"
                              value={item.amount}
                              onChange={e => update(idx, { ...item, amount: Number(e.target.value) })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                            <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                          </div>
                        )}
                      />
                      <button
                        type="button"
                        onClick={() => setExpenses(EXPENSE_SUGGESTIONS)}
                        className="mt-2 text-xs text-indigo-500 hover:text-indigo-700 transition"
                      >
                        Load example expenses
                      </button>
                    </div>
                  </>
                )}

                {/* Forecast extra fields */}
                {mode === "forecast" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Current Savings Balance ($)</label>
                      <input type="number" value={currentSavings} min={0} onChange={e => setCurrentSavings(Number(e.target.value))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Monthly Growth Rate (%)</label>
                      <input type="number" value={growthRate} step={0.1} min={0} onChange={e => setGrowthRate(Number(e.target.value))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                    </div>
                  </div>
                )}

                {/* Envelope mode */}
                {mode === "envelope" && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Envelopes</label>
                    <DynList
                      items={envelopes}
                      setItems={setEnvelopes}
                      empty={{ name: "", allocated: 0, spent: 0 }}
                      addLabel="Add Envelope"
                      renderRow={(item, idx, update, remove) => (
                        <div key={idx} className="flex gap-2 items-center">
                          <input placeholder="Name" value={item.name}
                            onChange={e => update(idx, { ...item, name: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                          <input type="number" placeholder="Allocated $" value={item.allocated}
                            onChange={e => update(idx, { ...item, allocated: Number(e.target.value) })}
                            className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                          <input type="number" placeholder="Spent $" value={item.spent}
                            onChange={e => update(idx, { ...item, spent: Number(e.target.value) })}
                            className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                          <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                        </div>
                      )}
                    />
                  </div>
                )}

                {/* Subscriptions mode */}
                {mode === "subscriptions" && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Subscriptions</label>
                    <DynList
                      items={subscriptions}
                      setItems={setSubscriptions}
                      empty={{ name: "", monthly_cost: 0, category: "Other", usage_per_week: 0 }}
                      addLabel="Add Subscription"
                      renderRow={(item, idx, update, remove) => (
                        <div key={idx} className="flex gap-2 items-center flex-wrap">
                          <input placeholder="Service name" value={item.name}
                            onChange={e => update(idx, { ...item, name: e.target.value })}
                            className="flex-1 min-w-[100px] px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                          <input type="number" placeholder="$/mo" value={item.monthly_cost}
                            onChange={e => update(idx, { ...item, monthly_cost: Number(e.target.value) })}
                            className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                          <input placeholder="Category" value={item.category}
                            onChange={e => update(idx, { ...item, category: e.target.value })}
                            className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                          <div className="flex items-center gap-1">
                            <input type="number" placeholder="Use/wk" value={item.usage_per_week} min={0} max={7}
                              onChange={e => update(idx, { ...item, usage_per_week: Number(e.target.value) })}
                              className="w-16 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <span className="text-xs text-gray-400">×/wk</span>
                          </div>
                          <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                        </div>
                      )}
                    />
                    <button
                      type="button"
                      onClick={() => setSubscriptions(SUB_SUGGESTIONS)}
                      className="mt-2 text-xs text-indigo-500 hover:text-indigo-700 transition"
                    >
                      Load example subscriptions
                    </button>
                  </div>
                )}

                {/* Net worth mode */}
                {mode === "net_worth" && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Assets</label>
                      <DynList
                        items={assets}
                        setItems={setAssets}
                        empty={{ name: "", value: 0, type: "Other" }}
                        addLabel="Add Asset"
                        renderRow={(item, idx, update, remove) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input placeholder="Asset name" value={item.name}
                              onChange={e => update(idx, { ...item, name: e.target.value })}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="Value $" value={item.value}
                              onChange={e => update(idx, { ...item, value: Number(e.target.value) })}
                              className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input placeholder="Type" value={item.type}
                              onChange={e => update(idx, { ...item, type: e.target.value })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Liabilities</label>
                      <DynList
                        items={liabilities}
                        setItems={setLiabilities}
                        empty={{ name: "", balance: 0, interest_rate: 0, type: "Other" }}
                        addLabel="Add Liability"
                        renderRow={(item, idx, update, remove) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input placeholder="Liability name" value={item.name}
                              onChange={e => update(idx, { ...item, name: e.target.value })}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="Balance $" value={item.balance}
                              onChange={e => update(idx, { ...item, balance: Number(e.target.value) })}
                              className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <div className="flex items-center gap-1">
                              <input type="number" placeholder="Rate" value={item.interest_rate} step={0.1}
                                onChange={e => update(idx, { ...item, interest_rate: Number(e.target.value) })}
                                className="w-16 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                              <span className="text-xs text-gray-400">%</span>
                            </div>
                            <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                          </div>
                        )}
                      />
                    </div>
                  </>
                )}

                {/* Debt payoff mode */}
                {mode === "debt_payoff" && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Debts</label>
                      <DynList
                        items={debts}
                        setItems={setDebts}
                        empty={{ name: "", balance: 0, apr: 0, min_payment: 0 }}
                        addLabel="Add Debt"
                        renderRow={(item, idx, update, remove) => (
                          <div key={idx} className="flex gap-2 items-center flex-wrap">
                            <input placeholder="Debt name" value={item.name}
                              onChange={e => update(idx, { ...item, name: e.target.value })}
                              className="flex-1 min-w-[120px] px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="Balance" value={item.balance}
                              onChange={e => update(idx, { ...item, balance: Number(e.target.value) })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="APR %" value={item.apr} step={0.1}
                              onChange={e => update(idx, { ...item, apr: Number(e.target.value) })}
                              className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="Min $" value={item.min_payment}
                              onChange={e => update(idx, { ...item, min_payment: Number(e.target.value) })}
                              className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                          </div>
                        )}
                      />
                      <button
                        type="button"
                        onClick={() => setDebts(DEBT_SUGGESTIONS)}
                        className="mt-2 text-xs text-indigo-500 hover:text-indigo-700 transition"
                      >
                        Load example debts
                      </button>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Extra Monthly Debt Payment ($)</label>
                      <input type="number" value={extraDebtPayment} min={0} onChange={e => setExtraDebtPayment(Number(e.target.value))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                    </div>
                  </>
                )}

                {/* Retirement / FIRE mode */}
                {mode === "retirement" && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Current Age</label>
                        <input type="number" value={currentAge} min={18} max={80} onChange={e => setCurrentAge(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Target Retirement Age</label>
                        <input type="number" value={retirementAge} min={30} max={85} onChange={e => setRetirementAge(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Current Savings ($)</label>
                        <input type="number" value={currentSavings} min={0} onChange={e => setCurrentSavings(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Monthly Contribution ($)</label>
                        <input type="number" value={monthlyContribution} min={0} onChange={e => setMonthlyContribution(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Annual Return (%)</label>
                        <input type="number" value={annualReturnPct} step={0.1} onChange={e => setAnnualReturnPct(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Withdrawal Rate (%)</label>
                        <input type="number" value={withdrawalRatePct} step={0.1} onChange={e => setWithdrawalRatePct(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Inflation (%)</label>
                        <input type="number" value={inflationRate} step={0.1} onChange={e => setInflationRate(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Annual Spending in Retirement ($)</label>
                      <input type="number" value={annualRetirementExpenses} min={0} onChange={e => setAnnualRetirementExpenses(Number(e.target.value))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                    </div>
                  </>
                )}

                {/* Savings goals mode */}
                {mode === "savings_goals" && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Savings Goals</label>
                      <DynList
                        items={goals}
                        setItems={setGoals}
                        empty={{ name: "", target_amount: 0, current_amount: 0, priority: 3, deadline_months: 12 }}
                        addLabel="Add Goal"
                        renderRow={(item, idx, update, remove) => (
                          <div key={idx} className="flex gap-2 items-center flex-wrap">
                            <input placeholder="Goal name" value={item.name}
                              onChange={e => update(idx, { ...item, name: e.target.value })}
                              className="flex-1 min-w-[120px] px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="Target" value={item.target_amount}
                              onChange={e => update(idx, { ...item, target_amount: Number(e.target.value) })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="Current" value={item.current_amount}
                              onChange={e => update(idx, { ...item, current_amount: Number(e.target.value) })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="Priority" value={item.priority} min={1} max={5}
                              onChange={e => update(idx, { ...item, priority: Number(e.target.value) })}
                              className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="Months" value={item.deadline_months} min={1}
                              onChange={e => update(idx, { ...item, deadline_months: Number(e.target.value) })}
                              className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                          </div>
                        )}
                      />
                      <button
                        type="button"
                        onClick={() => setGoals(GOAL_SUGGESTIONS)}
                        className="mt-2 text-xs text-indigo-500 hover:text-indigo-700 transition"
                      >
                        Load example goals
                      </button>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Monthly Savings Available ($)</label>
                      <input type="number" value={monthlySavingsAvailable} min={0} onChange={e => setMonthlySavingsAvailable(Number(e.target.value))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                    </div>
                  </>
                )}

                {/* Emergency fund mode */}
                {mode === "emergency_fund" && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Monthly Expenses ($)</label>
                        <input type="number" value={monthlyExpenses} min={0} onChange={e => setMonthlyExpenses(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Current Emergency Fund ($)</label>
                        <input type="number" value={emergencyCurrent} min={0} onChange={e => setEmergencyCurrent(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Monthly Contribution ($)</label>
                        <input type="number" value={monthlyContribution} min={0} onChange={e => setMonthlyContribution(Number(e.target.value))}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Job Sector</label>
                        <input type="text" value={jobSector} onChange={e => setJobSector(e.target.value)}
                          placeholder="employed, freelance, contractor..."
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                      </div>
                    </div>
                  </>
                )}

                {/* Transaction sync mode */}
                {mode === "transaction_sync" && (
                  <>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 flex items-center justify-between gap-2">
                      <p className="text-xs text-gray-600">Persist synced accounts/transactions to your secure workspace.</p>
                      <div className="flex gap-2">
                        <button type="button" onClick={loadTransactionWorkspace} disabled={workspaceBusy} className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-60">Refresh</button>
                        <button type="button" onClick={saveTransactionWorkspace} disabled={workspaceBusy} className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60">Save</button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Connected Accounts</label>
                      <DynList
                        items={connectedAccounts}
                        setItems={setConnectedAccounts}
                        empty={{ name: "", type: "bank" }}
                        addLabel="Add Account"
                        renderRow={(item, idx, update, remove) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input placeholder="Account name" value={item.name}
                              onChange={e => update(idx, { ...item, name: e.target.value })}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input placeholder="Type" value={item.type}
                              onChange={e => update(idx, { ...item, type: e.target.value })}
                              className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Synced Transactions</label>
                      <DynList
                        items={transactions}
                        setItems={setTransactions}
                        empty={{ merchant: "", amount: 0, category: "Other", account: "Checking", date: "2026-04-01" }}
                        addLabel="Add Transaction"
                        renderRow={(item, idx, update, remove) => (
                          <div key={idx} className="flex gap-2 items-center flex-wrap">
                            <input placeholder="Merchant" value={item.merchant}
                              onChange={e => update(idx, { ...item, merchant: e.target.value })}
                              className="flex-1 min-w-[110px] px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="Amount" value={item.amount}
                              onChange={e => update(idx, { ...item, amount: Number(e.target.value) })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input placeholder="Category" value={item.category}
                              onChange={e => update(idx, { ...item, category: e.target.value })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input placeholder="Account" value={item.account}
                              onChange={e => update(idx, { ...item, account: e.target.value })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                          </div>
                        )}
                      />
                      <button type="button" onClick={() => setTransactions(TXN_SUGGESTIONS)} className="mt-2 text-xs text-indigo-500 hover:text-indigo-700 transition">
                        Load example synced transactions
                      </button>
                    </div>
                  </>
                )}

                {/* Household sync mode */}
                {mode === "household_sync" && (
                  <>
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-gray-600">Collaborative household workspace with persisted categories, members, and alerts.</p>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => loadHouseholdWorkspace()} disabled={workspaceBusy} className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-700 hover:bg-white disabled:opacity-60">Refresh</button>
                          <button type="button" onClick={saveHouseholdWorkspace} disabled={workspaceBusy} className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60">Save</button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Household Name</label>
                        <input
                          type="text"
                          value={householdName}
                          onChange={(e) => setHouseholdName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        />
                        {householdId && <p className="text-[11px] text-gray-500 mt-1">Workspace ID: {householdId}</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Household Members</label>
                      <DynList
                        items={householdMembers}
                        setItems={setHouseholdMembers}
                        empty={{ name: "" }}
                        addLabel="Add Member"
                        renderRow={(item, idx, update, remove) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input placeholder="Member name" value={item.name}
                              onChange={e => update(idx, { ...item, name: e.target.value })}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Shared Categories</label>
                      <DynList
                        items={sharedCategories}
                        setItems={setSharedCategories}
                        empty={{ name: "", limit: 0 }}
                        addLabel="Add Shared Category"
                        renderRow={(item, idx, update, remove) => (
                          <div key={idx} className="flex gap-2 items-center">
                            <input placeholder="Category" value={item.name}
                              onChange={e => update(idx, { ...item, name: e.target.value })}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="Limit" value={item.limit}
                              onChange={e => update(idx, { ...item, limit: Number(e.target.value) })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Shared Transactions</label>
                      <DynList
                        items={sharedTransactions}
                        setItems={setSharedTransactions}
                        empty={{ member: "", category: "", amount: 0, date: "2026-04-01", note: "" }}
                        addLabel="Add Shared Transaction"
                        renderRow={(item, idx, update, remove) => (
                          <div key={idx} className="flex gap-2 items-center flex-wrap">
                            <input placeholder="Member" value={item.member}
                              onChange={e => update(idx, { ...item, member: e.target.value })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input placeholder="Category" value={item.category}
                              onChange={e => update(idx, { ...item, category: e.target.value })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input type="number" placeholder="Amount" value={item.amount}
                              onChange={e => update(idx, { ...item, amount: Number(e.target.value) })}
                              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <input placeholder="Note" value={item.note || ""}
                              onChange={e => update(idx, { ...item, note: e.target.value })}
                              className="flex-1 min-w-[90px] px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                            <button onClick={() => remove(idx)} className="text-gray-400 hover:text-red-500 transition text-lg">×</button>
                          </div>
                        )}
                      />
                      <button type="button" onClick={() => { setHouseholdMembers(HOUSEHOLD_MEMBER_SUGGESTIONS); setSharedCategories(SHARED_CATEGORY_SUGGESTIONS); setSharedTransactions(SHARED_TXN_SUGGESTIONS); }} className="mt-2 text-xs text-indigo-500 hover:text-indigo-700 transition">
                        Load household example
                      </button>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Large Purchase Alert Threshold ($)</label>
                      <input type="number" value={largePurchaseThreshold} min={0} onChange={e => setLargePurchaseThreshold(Number(e.target.value))}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 text-sm" />
                    </div>
                    {householdNotifications.length > 0 && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <p className="text-xs font-semibold text-amber-800 mb-2">Recent Household Alerts</p>
                        <div className="space-y-1.5">
                          {householdNotifications.slice(0, 5).map((n: any) => (
                            <p key={n.id} className="text-xs text-amber-700">{n.message}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Run button */}
                <button
                  onClick={handleRun}
                  disabled={running}
                  className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-60 transition text-base flex items-center justify-center gap-2 shadow-md"
                >
                  {running ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Analyzing…</>
                  ) : (
                    <><span>⚡</span> Run Analysis</>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* ── Right: Results panel ── */}
          <div ref={resultsRef} className="w-full xl:flex-1 min-w-0">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-5 mb-6 flex gap-3">
                <span className="text-xl">⚠️</span>
                <div><p className="font-semibold">Error</p><p className="text-sm mt-0.5">{error}</p></div>
              </div>
            )}

            {!result && !error && (
              <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-16 text-center text-gray-400">
                <p className="text-6xl mb-4">💰</p>
                <p className="font-semibold text-gray-600 text-xl mb-2">Your analysis will appear here</p>
                <p className="text-sm max-w-sm mx-auto">
                  Select a tab, fill in your numbers, and click <strong>Run Analysis</strong> to get personalized insights.
                </p>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl mx-auto">
                  {TABS.map(t => (
                    <button key={t.mode} onClick={() => setMode(t.mode)}
                      className={`p-3 rounded-xl border text-center transition cursor-pointer ${mode === t.mode ? "border-indigo-400 bg-indigo-50" : "border-gray-200 hover:border-indigo-300 hover:bg-gray-50"}`}>
                      <div className="text-2xl mb-1">{t.icon}</div>
                      <div className="text-xs font-medium text-gray-600 leading-tight">{t.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {result && !error && (
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <h2 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
                      <span>{activeTab.icon}</span> {activeTab.label} — Results
                    </h2>
                    {result.duration_ms && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{result.duration_ms}ms</span>
                    )}
                  </div>
                  <button onClick={() => setResult(null)} className="text-gray-400 hover:text-gray-700 text-sm">✕ Clear</button>
                </div>
                <div className="p-6 lg:p-8">
                  {(() => {
                    const d = result?.result?.data ?? result?.data;
                    if (!d) return <pre className="text-xs text-gray-500 overflow-auto">{JSON.stringify(result, null, 2)}</pre>;
                    switch (result?.result?.mode || result?.mode) {
                      case "zero_based": return <ZeroBasedResults data={d} />;
                      case "envelope": return <EnvelopeResults data={d} />;
                      case "subscriptions": return <SubscriptionResults data={d} />;
                      case "forecast": return <ForecastResults data={d} />;
                      case "net_worth": return <NetWorthResults data={d} />;
                      case "debt_payoff": return <DebtPayoffResults data={d} />;
                      case "retirement": return <RetirementResults data={d} />;
                      case "savings_goals": return <SavingsGoalsResults data={d} />;
                      case "emergency_fund": return <EmergencyFundResults data={d} />;
                      case "transaction_sync": return <TransactionSyncResults data={d} />;
                      case "household_sync": return <HouseholdSyncResults data={d} />;
                      default: return <pre className="text-xs text-gray-500 overflow-auto">{JSON.stringify(d, null, 2)}</pre>;
                    }
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
